import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { EmptyState } from '@/components/EmptyState';
import {
  facebookPageService,
  FbPageItem,
  FbPageListResponse,
} from '@/services/facebook-page.service';
import { FbPagePostInsightsModal } from '@/components/facebook/FbPagePostInsightsModal';
import {
  Image as ImageIcon, Heart, MessageCircle, Share2, RefreshCw, AlertCircle, ExternalLink,
} from 'lucide-react';

type Props = {
  /** Rendered inside a TabbedPage shell — suppress the page Topbar. */
  embedded?: boolean;
};

export default function FacebookPageOverview({ embedded = false }: Props = {}) {
  const [posts, setPosts] = useState<FbPageListResponse | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [insightsPost, setInsightsPost] = useState<FbPageItem | null>(null);

  const load = async (force = false) => {
    setRefreshing(true);
    setError(null);
    try {
      const r = await facebookPageService.posts(force, 24);
      setPosts(r);
    } catch (e: any) {
      const reason: any = e;
      setError(reason?.response?.data?.message ?? 'Failed to load Page posts');
    } finally { setRefreshing(false); }
  };

  useEffect(() => { load(false); }, []);

  if (error) {
    return (
      <>
        {!embedded && <Topbar title="Facebook Page" breadcrumb="Facebook Ads" />}
        <div className={clsx('w-full px-7', embedded ? 'pb-24 pt-4' : 'py-7')}>
          <EmptyState
            icon={<AlertCircle size={40} className="text-warning" />}
            title="Couldn't load Page posts"
            description={error}
            action={
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Button onClick={() => load(true)} loading={refreshing}>
                  <RefreshCw size={14} /> Retry
                </Button>
                <Link to="/facebook/ad-account">
                  <Button variant="secondary"><ExternalLink size={14} /> Connect a Page</Button>
                </Link>
              </div>
            }
          />
        </div>
      </>
    );
  }

  return (
    <>
      {embedded ? (
        <div className="flex flex-wrap items-center justify-end gap-2 px-7 pt-4">
          <div className="flex gap-2">
            <Button variant="primary" onClick={() => load(true)} loading={refreshing}>
              <RefreshCw size={14} /> Refresh
            </Button>
          </div>
        </div>
      ) : (
        <Topbar
          title={posts?.pageName ? `${posts.pageName} — Posts` : 'Facebook Page'}
          breadcrumb="Facebook Ads"
          description={posts?.pageId ? `Page ID ${posts.pageId} · click any post to see insights from Meta.` : 'Recent organic Page posts.'}
          right={
            <div className="flex gap-2">
              <Button variant="primary" onClick={() => load(true)} loading={refreshing}>
                <RefreshCw size={14} /> Refresh
              </Button>
            </div>
          }
        />
      )}

      <div className={clsx('w-full space-y-4 px-7', embedded ? 'pb-24 pt-4' : 'py-7')}>
        {posts === null && (
          <div className="grid grid-cols-3 gap-4 lg:grid-cols-4">
            {[...Array(8)].map((_, i) => <div key={i} className="skeleton aspect-square rounded-2xl" />)}
          </div>
        )}

        {posts && posts.data.length === 0 && (
          <EmptyState
            icon={<ImageIcon size={40} className="text-text-muted" />}
            title="No posts yet"
            description="Publish content from Facebook directly, then refresh here to see the insights."
            action={
              <a href={`https://facebook.com/${posts.pageId}`} target="_blank" rel="noreferrer">
                <Button variant="secondary"><ExternalLink size={14} /> Open Page on Facebook</Button>
              </a>
            }
          />
        )}

        {posts && posts.data.length > 0 && (
          <div className="grid grid-cols-3 gap-4 lg:grid-cols-4">
            {posts.data.map((p) => (
              <PostCard key={p.id} post={p} onClick={() => setInsightsPost(p)} />
            ))}
          </div>
        )}

        {posts && posts.fromCache && (
          <div className="rounded-xl border border-border bg-surface px-4 py-2.5 text-xs text-text-muted">
            Showing cached snapshot. Click <strong>Refresh</strong> to pull live from Meta.
          </div>
        )}
      </div>

      <FbPagePostInsightsModal
        open={!!insightsPost}
        onClose={() => setInsightsPost(null)}
        post={insightsPost}
        onDeleted={(deletedId) => {
          setPosts((prev) => prev ? { ...prev, data: prev.data.filter((p) => p.id !== deletedId) } : prev);
          load(true).catch(() => undefined);
        }}
      />
    </>
  );
}

const PostCard: React.FC<{ post: FbPageItem; onClick: () => void }> = ({ post, onClick }) => {
  const reactions = post.reactions?.summary?.total_count ?? 0;
  const comments = post.comments?.summary?.total_count ?? 0;
  const shares = post.shares?.count ?? 0;
  const isVideo = post.status_type === 'added_video'
    || post.attachments?.data?.[0]?.media_type === 'video';

  return (
    <button
      type="button"
      onClick={onClick}
      className="group block w-full text-left overflow-hidden rounded-2xl border border-border bg-surface shadow-soft transition-all duration-200 hover:-translate-y-0.5 hover:shadow-elevated focus:outline-none focus:ring-2 focus:ring-primary"
    >
      <div className="relative aspect-square bg-surface-alt">
        {(post.imagekit_url || post.full_picture) ? (
          // Prefer ImageKit mirror — `full_picture` is an FB-signed URL that
          // expires in a few hours, leaving blank tiles otherwise.
          <img src={post.imagekit_url || post.full_picture} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-text-muted">
            <ImageIcon size={40} />
          </div>
        )}
        <div className="absolute inset-0 flex items-center justify-center gap-3 bg-black/60 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
          <span className="flex items-center gap-1 text-white text-sm font-semibold">
            <Heart size={14} className="fill-current" />
            {reactions.toLocaleString()}
          </span>
          <span className="flex items-center gap-1 text-white text-sm font-semibold">
            <MessageCircle size={14} />
            {comments.toLocaleString()}
          </span>
          <span className="flex items-center gap-1 text-white text-sm font-semibold">
            <Share2 size={14} />
            {shares.toLocaleString()}
          </span>
        </div>
        {(isVideo || post.status_type) && (
          <div className="absolute top-2 right-2 rounded-full bg-black/60 px-2 py-0.5 text-[10px] text-white">
            {isVideo ? 'VIDEO' : (post.status_type ?? 'POST')}
          </div>
        )}
      </div>

      <Card className="rounded-none rounded-b-2xl border-x-0 border-b-0 border-t border-border">
        {post.message && (
          <p className="mb-1.5 line-clamp-2 text-xs text-text-primary">{post.message}</p>
        )}
        <div className="text-[10px] text-text-muted">
          {post.created_time ? new Date(post.created_time).toLocaleDateString() : ''}
        </div>
      </Card>
    </button>
  );
};
